import React, { useRef, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import { ChevronRight, Salad, UploadCloud } from "lucide-react";

import { Button } from "@/components/ui/button";
import { adminFetch } from "@/hooks/use-admin-session";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type DietProgramActivityDraft = {
  activity_id: string;
  title: string;
  description: string;
  image_url: string;
  days: number[];
  time_of_day: string;
};

export type DietDraft = {
  xpPoints: string;
  id?: string;
  title: string;
  description: string;
  coverImageUrl: string;
  waterTargetLiters: string;
  mealType: string;
  programDays: string;
  active: boolean;
  isPremium: boolean;
  tagsText: string;
  programActivities: DietProgramActivityDraft[];
  notes: string;
  price: string;
  store_product_id?: string | null;
  kategori_id?: string;
  title_en?: string;
  title_es?: string;
  description_en?: string;
  description_es?: string;
};

// Google Play / RevenueCat'te tanımlı 3 sabit tek seferlik içerik fiyat kademesi.
const PRICE_TIERS = [
  { price: 999, storeProductId: 'icerik_999', label: '₺999,00 (icerik_999)' },
  { price: 1299, storeProductId: 'icerik_1299', label: '₺1.299,00 (icerik_1299)' },
  { price: 1499, storeProductId: 'icerik_1499', label: '₺1.499,00 (icerik_1499)' },
] as const;

export function NumerolojiDietEditor({
  draft,
  setDraft,
  onClose,
  onSave,
  isSaving,
}: {
  draft: DietDraft;
  setDraft: (next: DietDraft) => void;
  onClose: () => void;
  onSave: (payload: any) => void;
  isSaving: boolean;
}) {
  const isEdit = Boolean(draft.id);
  const title = isEdit ? "Diyeti Düzenle" : "Yeni Diyet Ekle";
  const { data: kategorilerData } = useQuery({
    queryKey: ['kategoriler', 'diyet'],
    queryFn: () => adminFetch('/api/admin/kategoriler?tur=diyet').then(r => r.json()),
  });
  const kategoriler: any[] = kategorilerData?.items ?? [];
  const coverFileRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [activeI18nLang, setActiveI18nLang] = useState<'en' | 'es' | null>(null);
  const activityFileRef = useRef<HTMLInputElement | null>(null);
  const activityUploadIndexRef = useRef<number>(-1);
  const [uploadingActivityIndex, setUploadingActivityIndex] = useState<number | null>(null);
  const [activityUploadError, setActivityUploadError] = useState<string | null>(null);

  const uploadCoverImage = async (file: File) => {
    setMediaError(null);

    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setMediaError("Kapak görseli için max 6MB dosya yükleyebilirsiniz.");
      return;
    }

    setIsUploadingCover(true);

    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Dosya okunamadı."));
        reader.onload = () => {
          const result = typeof reader.result === "string" ? reader.result : "";
          const comma = result.indexOf(",");
          if (comma === -1) return reject(new Error("Dosya okunamadı."));
          resolve(result.slice(comma + 1));
        };
        reader.readAsDataURL(file);
      });

      const res = await adminFetch("/api/admin/numeroloji/diets/image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contentType: file.type || "image/jpeg",
          dataBase64,
          dietId: draft.id,
          dietTitle: draft.title,
        }),
      });

      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Resim yüklenemedi.");
      }

      const url = typeof json?.url === "string" ? json.url : "";
      if (!url) throw new Error("Resim URL alınamadı.");

      setDraft({ ...draft, coverImageUrl: url });
    } catch (err) {
      setMediaError(err instanceof Error ? err.message : "Resim yüklenemedi.");
    } finally {
      setIsUploadingCover(false);
    }
  };

  const savePayload = () => {
    const water = draft.waterTargetLiters.trim();
    const programDaysRaw = String(draft.programDays ?? "").trim();
    const parsedProgramDays = Number(programDaysRaw);

    onSave({
      id: draft.id,
      title: draft.title,
      description: draft.description,
      coverImageUrl: draft.coverImageUrl.trim() || undefined,
      waterTargetLiters: water ? Number(water) : undefined,
      mealType: draft.mealType.trim() || undefined,
      programDays: Number.isFinite(parsedProgramDays) && parsedProgramDays > 0 ? Math.round(parsedProgramDays) : undefined,
      xpPoints: draft.xpPoints,
      active: draft.active,
      isPremium: draft.isPremium,
      programActivities: draft.programActivities,
      notes: draft.notes,
      price: parseInt(draft.price) || 0,
      store_product_id: draft.store_product_id || null,
      tags: draft.tagsText
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      kategori_id: draft.kategori_id || null,
    });
  };

  const baseInput =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-50";

  const formDisabled = Boolean(isSaving || isUploadingCover || uploadingActivityIndex !== null);

  const uploadActivityImage = async (file: File, idx: number) => {
    setActivityUploadError(null);
    if (file.size > 6 * 1024 * 1024) { setActivityUploadError("Max 6MB."); return; }
    setUploadingActivityIndex(idx);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Dosya okunamadı."));
        reader.onload = () => {
          const result = typeof reader.result === "string" ? reader.result : "";
          const comma = result.indexOf(",");
          if (comma === -1) return reject(new Error("Dosya okunamadı."));
          resolve(result.slice(comma + 1));
        };
        reader.readAsDataURL(file);
      });
      const res = await adminFetch("/api/admin/numeroloji/diets/image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contentType: file.type || "image/jpeg", dataBase64, dietId: draft.id, dietTitle: draft.title }),
      });
      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) throw new Error(typeof json?.error === "string" ? json.error : "Resim yüklenemedi.");
      const url = typeof json?.url === "string" ? json.url : "";
      if (!url) throw new Error("Resim URL alınamadı.");
      const updated = draft.programActivities.map((a, i) => i === idx ? { ...a, image_url: url } : a);
      setDraft({ ...draft, programActivities: updated });
    } catch (err) {
      setActivityUploadError(err instanceof Error ? err.message : "Resim yüklenemedi.");
    } finally {
      setUploadingActivityIndex(null);
    }
  };

  const addActivity = () => {
    setDraft({
      ...draft,
      programActivities: [
        ...draft.programActivities,
        { activity_id: `act_${Date.now()}`, title: "", description: "", image_url: "", days: [], time_of_day: "sabah" },
      ],
    });
  };

  const removeActivity = (idx: number) => {
    setDraft({ ...draft, programActivities: draft.programActivities.filter((_, i) => i !== idx) });
  };

  const updateActivity = (idx: number, patch: Partial<DietProgramActivityDraft>) => {
    setDraft({ ...draft, programActivities: draft.programActivities.map((a, i) => i === idx ? { ...a, ...patch } : a) });
  };

  const totalDays = Math.max(1, Math.round(Number(draft.programDays) || 7));
  const [rangeInputs, setRangeInputs] = useState<Record<number, { start: string; end: string; manual: string }>>({});
  const [notesPreview, setNotesPreview] = useState(false);
  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  const insertMarkdown = useCallback((prefix: string, suffix = "") => {
    const ta = notesRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const selected = ta.value.slice(start, end);
    const before = ta.value.slice(0, start);
    const after = ta.value.slice(end);
    const inserted = `${prefix}${selected || "metin"}${suffix}`;
    const next = `${before}${inserted}${after}`;
    setDraft({ ...draft, notes: next });
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, start + prefix.length + (selected || "metin").length);
    }, 0);
  }, [draft, setDraft]);
  const getRangeInput = (idx: number) => rangeInputs[idx] ?? { start: "", end: "", manual: "" };
  const setRangeInput = (idx: number, patch: Partial<{ start: string; end: string; manual: string }>) =>
    setRangeInputs((prev) => ({ ...prev, [idx]: { ...getRangeInput(idx), ...patch } }));

  const renderInline = (text: string): React.ReactNode => {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map((part, i) => {
      if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
      if (/^\*[^*]+\*$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
      return part;
    });
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white/70 p-4 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/40 sm:p-6">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
          <span>İçerik Yönetimi</span>
          <ChevronRight className="h-4 w-4" />
          <span>Diyetler</span>
          <ChevronRight className="h-4 w-4" />
          <span className="text-slate-900 dark:text-slate-100">{title}</span>
        </div>

        <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{title}</div>
            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Diyet planını etiketleyerek kullanıcıya özel görünür hale getirebilirsiniz.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              className="rounded-full bg-white/80 dark:bg-slate-950/40"
              onClick={onClose}
              disabled={formDisabled}
            >
              Vazgeç
            </Button>
            <Button className="rounded-full" onClick={savePayload} disabled={formDisabled}>
              {isSaving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Genel Bilgiler</div>

            <div className="mt-5 grid gap-5">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Başlık</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Örn: Denge ve Arınma"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Açıklama</Label>
                <Textarea
                  className={cn(baseInput, "min-h-[180px] resize-none")}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Diyetin kısa açıklaması..."
                />
              </div>

              <div className="border-t border-slate-100 pt-4 dark:border-slate-800">
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Çeviriler</span>
                  <div className="flex gap-1">
                    {(["en", "es"] as const).map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => setActiveI18nLang(activeI18nLang === lang ? null : lang)}
                        className={cn(
                          "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                          activeI18nLang === lang
                            ? "bg-blue-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700",
                        )}
                      >
                        {lang === "en" ? "🇬🇧 EN" : "🇪🇸 ES"}
                      </button>
                    ))}
                  </div>
                </div>
                {activeI18nLang && (
                  <div className="grid gap-4">
                    <div className="grid gap-2">
                      <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Başlık ({activeI18nLang.toUpperCase()})
                      </Label>
                      <Input
                        className={cn(baseInput)}
                        value={activeI18nLang === "en" ? (draft.title_en ?? "") : (draft.title_es ?? "")}
                        onChange={(e) => setDraft({ ...draft, [`title_${activeI18nLang}`]: e.target.value })}
                        placeholder={activeI18nLang === "en" ? "English title..." : "Título en español..."}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Açıklama ({activeI18nLang.toUpperCase()})
                      </Label>
                      <Textarea
                        className={cn(baseInput, "min-h-[100px] resize-none")}
                        value={activeI18nLang === "en" ? (draft.description_en ?? "") : (draft.description_es ?? "")}
                        onChange={(e) => setDraft({ ...draft, [`description_${activeI18nLang}`]: e.target.value })}
                        placeholder={activeI18nLang === "en" ? "English description..." : "Descripción en español..."}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Detaylar</div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Su hedefi (L)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="decimal"
                  value={draft.waterTargetLiters}
                  onChange={(e) => setDraft({ ...draft, waterTargetLiters: e.target.value })}
                  placeholder="Örn: 2.5"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Öğün tipi (opsiyonel)</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.mealType}
                  onChange={(e) => setDraft({ ...draft, mealType: e.target.value })}
                  placeholder="Örn: Vegan / Hafif / Detoks"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Program Süresi (Gün)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="numeric"
                  value={draft.programDays}
                  onChange={(e) => setDraft({ ...draft, programDays: e.target.value })}
                  placeholder="Örn: 21 veya 40"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tamamlama Puanı (XP)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="numeric"
                  value={draft.xpPoints}
                  onChange={(e) => setDraft({ ...draft, xpPoints: e.target.value.replace(/[^0-9]/g, "") })}
                  placeholder="Örn: 5"
                />
              </div>

              <div className="grid gap-2 sm:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Etiketler (virgülle)</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.tagsText}
                  onChange={(e) => setDraft({ ...draft, tagsText: e.target.value })}
                  placeholder="genel, karmikborc1"
                />
              </div>

              <div className="grid gap-2 sm:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kategori</Label>
                <select
                  key={kategoriler.length}
                  className={cn(baseInput)}
                  value={draft.kategori_id ?? ''}
                  onChange={(e) => setDraft({ ...draft, kategori_id: e.target.value || undefined })}
                >
                  <option value="">— Kategori seç —</option>
                  {kategoriler.map((k: any) => (
                    <option key={k.id} value={k.id}>{k.isim}</option>
                  ))}
                </select>
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="flex items-center justify-between gap-3">
              <div className="text-base font-bold text-slate-900 dark:text-white">Medya</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">(FTP yükleme + URL)</div>
            </div>

            <div className="mt-5 grid gap-5">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kapak görsel URL</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.coverImageUrl}
                  onChange={(e) => setDraft({ ...draft, coverImageUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <input
                ref={coverFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  void uploadCoverImage(file);
                  e.currentTarget.value = "";
                }}
              />

              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-4 text-slate-600 dark:border-slate-800 dark:bg-slate-950/20 dark:text-slate-300">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full bg-white/80 gap-2 dark:bg-slate-950/40"
                  onClick={() => coverFileRef.current?.click()}
                  disabled={formDisabled}
                >
                  <UploadCloud className="h-4 w-4" />
                  {isUploadingCover ? "Kapak yükleniyor..." : "Kapak görseli yükle"}
                </Button>

                <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Dosya FTP'ye yüklenir ve URL alanı otomatik doldurulur.
                </div>

                {mediaError ? <div className="mt-2 text-xs text-destructive">{mediaError}</div> : null}
              </div>
            </div>
          </Card>
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="flex items-center justify-between gap-3">
              <div className="text-base font-bold text-slate-900 dark:text-white">Program Aktiviteleri</div>
              <Button type="button" variant="outline" className="rounded-full gap-1 text-xs" onClick={addActivity} disabled={formDisabled}>
                + Aktivite Ekle
              </Button>
            </div>

            <input
              ref={activityFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                void uploadActivityImage(file, activityUploadIndexRef.current);
                e.currentTarget.value = "";
              }}
            />

            {activityUploadError && <div className="mt-2 text-xs text-destructive">{activityUploadError}</div>}

            <div className="mt-4 space-y-4">
              {draft.programActivities.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-200 p-4 text-center text-sm text-slate-400 dark:border-slate-700">
                  Henüz aktivite eklenmedi.
                </div>
              )}
              {draft.programActivities.map((act, idx) => (
                <div key={act.activity_id} className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4 dark:border-slate-700 dark:bg-slate-900/30">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400">#{idx + 1}</span>
                    <button type="button" className="text-xs text-destructive hover:underline" onClick={() => removeActivity(idx)} disabled={formDisabled}>
                      × Kaldır
                    </button>
                  </div>

                  <div className="grid gap-3">
                    <div className="grid gap-1">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Başlık</Label>
                      <Input className={cn(baseInput, "text-sm")} value={act.title} onChange={(e) => updateActivity(idx, { title: e.target.value })} placeholder="Aktivite başlığı" disabled={formDisabled} />
                    </div>

                    <div className="grid gap-1">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Açıklama</Label>
                      <textarea className={cn(baseInput, "min-h-[60px] resize-none text-sm")} value={act.description} onChange={(e) => updateActivity(idx, { description: e.target.value })} placeholder="Kısa açıklama..." disabled={formDisabled} />
                    </div>

                    <div className="grid gap-1">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Görsel URL</Label>
                      <div className="flex gap-2">
                        <Input className={cn(baseInput, "text-sm flex-1")} value={act.image_url} onChange={(e) => updateActivity(idx, { image_url: e.target.value })} placeholder="https://..." disabled={formDisabled} />
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-full shrink-0 text-xs gap-1"
                          onClick={() => { activityUploadIndexRef.current = idx; activityFileRef.current?.click(); }}
                          disabled={formDisabled}
                        >
                          <UploadCloud className="h-3 w-3" />
                          {uploadingActivityIndex === idx ? "Yükleniyor..." : "Yükle"}
                        </Button>
                      </div>
                      {act.image_url && <img src={act.image_url} alt="" className="mt-1 h-16 w-28 rounded-xl object-cover" />}
                    </div>

                    <div className="grid gap-1">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Günün Zamanı</Label>
                      <select
                        className={cn(baseInput, "text-sm")}
                        value={act.time_of_day}
                        onChange={(e) => updateActivity(idx, { time_of_day: e.target.value })}
                        disabled={formDisabled}
                      >
                        {["sabah", "öğle", "ikindi", "akşam", "gece", "her zaman"].map((t) => (
                          <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid gap-3">
                      <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Günler</Label>

                      {/* Satır 1: Her Gün toggle */}
                      <button
                        type="button"
                        className={cn("w-fit rounded-full px-3 py-1 text-xs font-semibold border transition-colors",
                          act.days.length === totalDays
                            ? "bg-blue-600 text-white border-blue-600"
                            : "border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300")}
                        onClick={() => {
                          const all = Array.from({ length: totalDays }, (_, i) => i + 1);
                          updateActivity(idx, { days: act.days.length === totalDays ? [] : all });
                        }}
                        disabled={formDisabled}
                      >
                        Her Gün
                      </button>

                      {/* Satır 2: Gün Aralığı Ekle */}
                      <div className="grid gap-1">
                        <Label className="text-xs text-slate-500 dark:text-slate-400">Gün Aralığı Ekle</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={1}
                            max={totalDays}
                            placeholder="1"
                            value={getRangeInput(idx).start}
                            onChange={(e) => setRangeInput(idx, { start: e.target.value })}
                            disabled={formDisabled}
                            className="w-16 rounded-lg border border-slate-300 bg-slate-50 px-2 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                          />
                          <span className="text-xs text-slate-400">ile</span>
                          <input
                            type="number"
                            min={1}
                            max={totalDays}
                            placeholder="7"
                            value={getRangeInput(idx).end}
                            onChange={(e) => setRangeInput(idx, { end: e.target.value })}
                            disabled={formDisabled}
                            className="w-16 rounded-lg border border-slate-300 bg-slate-50 px-2 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                          />
                          <span className="text-xs text-slate-400">arası günler</span>
                          <button
                            type="button"
                            className="rounded-full px-3 py-1.5 text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors"
                            disabled={formDisabled}
                            onClick={() => {
                              const s = parseInt(getRangeInput(idx).start);
                              const e = parseInt(getRangeInput(idx).end);
                              if (!Number.isFinite(s) || !Number.isFinite(e) || s > e) return;
                              const range = Array.from({ length: e - s + 1 }, (_, i) => s + i);
                              const merged = Array.from(new Set([...act.days, ...range])).sort((a, b) => a - b);
                              updateActivity(idx, { days: merged });
                              setRangeInput(idx, { start: "", end: "" });
                            }}
                          >
                            Ekle
                          </button>
                        </div>
                      </div>

                      {/* Satır 3: Tek Gün Ekle */}
                      <div className="grid gap-1">
                        <Label className="text-xs text-slate-500 dark:text-slate-400">Tek Gün Ekle</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={1}
                            max={totalDays}
                            placeholder="Gün numarası (örn: 3)"
                            value={getRangeInput(idx).manual}
                            onChange={(e) => setRangeInput(idx, { manual: e.target.value })}
                            disabled={formDisabled}
                            className="w-48 rounded-lg border border-slate-300 bg-slate-50 px-2 py-1.5 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                          />
                          <button
                            type="button"
                            className="rounded-full px-3 py-1.5 text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors"
                            disabled={formDisabled}
                            onClick={() => {
                              const d = parseInt(getRangeInput(idx).manual);
                              if (!Number.isFinite(d) || d < 1) return;
                              const merged = Array.from(new Set([...act.days, d])).sort((a, b) => a - b);
                              updateActivity(idx, { days: merged });
                              setRangeInput(idx, { manual: "" });
                            }}
                          >
                            Ekle
                          </button>
                        </div>
                      </div>

                      {/* Satır 4: Seçili Günler */}
                      <div className="grid gap-1">
                        <Label className="text-xs text-slate-500 dark:text-slate-400">
                          Seçili Günler ({act.days.length} gün)
                        </Label>
                        <div className="flex flex-wrap gap-1.5">
                          {act.days.map((day) => (
                            <span
                              key={day}
                              className="inline-flex items-center gap-1 rounded-full bg-blue-600/15 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:text-blue-300"
                            >
                              {day}
                              <button
                                type="button"
                                className="hover:text-destructive"
                                disabled={formDisabled}
                                onClick={() => updateActivity(idx, { days: act.days.filter((d) => d !== day) })}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          {act.days.length === 0 && (
                            <span className="text-xs text-slate-400 dark:text-slate-500">Henüz gün seçilmedi</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-base font-bold text-slate-900 dark:text-white">Program Notları & Uyarılar</div>
                <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  Kullanıcı aktivite yaparken görecek bilgiler
                </div>
              </div>
              <button
                type="button"
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-semibold border transition-colors",
                  notesPreview
                    ? "bg-slate-800 text-white border-slate-800 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200"
                    : "border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300"
                )}
                onClick={() => setNotesPreview((v) => !v)}
              >
                {notesPreview ? "Düzenle" : "Önizleme"}
              </button>
            </div>

            <div className="mt-4">
              {notesPreview ? (
                <div className="min-h-[200px] rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200">
                  {draft.notes
                    ? draft.notes.split("\n").map((line, i) => {
                        if (/^## (.+)/.test(line)) {
                          return <div key={i} className="mb-2 text-base font-bold text-slate-900 dark:text-white">{line.replace(/^## /, "")}</div>;
                        }
                        if (/^# (.+)/.test(line)) {
                          return <div key={i} className="mb-2 text-lg font-extrabold text-slate-900 dark:text-white">{line.replace(/^# /, "")}</div>;
                        }
                        if (/^- (.+)/.test(line)) {
                          return (
                            <div key={i} className="flex items-start gap-1.5 mb-1">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-500" />
                              <span>{renderInline(line.replace(/^- /, ""))}</span>
                            </div>
                          );
                        }
                        if (line.trim() === "") return <div key={i} className="mb-2" />;
                        return <div key={i} className="mb-1">{renderInline(line)}</div>;
                      })
                    : <span className="text-slate-400">Henüz not yok.</span>
                  }
                </div>
              ) : (
                <>
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {[
                      { label: "K", title: "Kalın", prefix: "**", suffix: "**" },
                      { label: "İ", title: "İtalik", prefix: "*", suffix: "*" },
                      { label: "• Liste", title: "Madde", prefix: "- ", suffix: "" },
                      { label: "## Başlık", title: "Başlık", prefix: "## ", suffix: "" },
                    ].map((btn) => (
                      <button
                        key={btn.label}
                        type="button"
                        title={btn.title}
                        disabled={formDisabled}
                        className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                        onClick={() => insertMarkdown(btn.prefix, btn.suffix)}
                      >
                        {btn.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    ref={notesRef}
                    className={cn(baseInput, "min-h-[200px] resize-y font-mono text-sm")}
                    value={draft.notes}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    placeholder={"## Genel Kurallar\n- Şeker tüketimini azaltın\n- Bol su için\n\n**Önemli:** Sabah aç karna uygulayın."}
                    disabled={formDisabled}
                  />
                </>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Durum</div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Aktif</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Pasif ise kullanıcılar görmez.</div>
              </div>

              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={draft.active}
                  onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                />
                <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
              </label>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Premium</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Sadece premium üyeler görebilir.</div>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={draft.isPremium}
                  onChange={(e) => setDraft({ ...draft, isPremium: e.target.checked })}
                />
                <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-amber-500 peer-checked:after:translate-x-full dark:bg-slate-800" />
              </label>
            </div>

            <div className="mt-4 grid gap-1.5">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Ürün Kademesi</label>
              <select
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none focus:ring-4 focus:ring-blue-600/20 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-50"
                value={draft.store_product_id ?? (parseInt(draft.price) ? "custom" : "free")}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "free") {
                    setDraft({ ...draft, price: "0", store_product_id: null });
                  } else if (v === "custom") {
                    setDraft({ ...draft, store_product_id: null });
                  } else {
                    const tier = PRICE_TIERS.find((t) => t.storeProductId === v);
                    if (tier) setDraft({ ...draft, price: String(tier.price), store_product_id: tier.storeProductId });
                  }
                }}
              >
                <option value="free">Ücretsiz</option>
                {PRICE_TIERS.map((t) => (
                  <option key={t.storeProductId} value={t.storeProductId}>{t.label}</option>
                ))}
                <option value="custom">Serbest / Elle gir</option>
              </select>
            </div>

            <div className="mt-4 grid gap-1.5">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Fiyat (₺) — 0 ise ücretsiz</label>
              <input
                type="number"
                min={0}
                value={draft.price}
                disabled={Boolean(draft.store_product_id)}
                onChange={(e) => setDraft({ ...draft, price: e.target.value.replace(/[^0-9]/g, "") })}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none focus:ring-4 focus:ring-blue-600/20 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-50"
                placeholder="0"
              />
            </div>

            <div
              className={cn(
                "mt-4 inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
                draft.active
                  ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                  : "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300",
              )}
            >
              {draft.active ? "Yayında" : "Taslak"}
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-white">
              <Salad className="h-4 w-4" />
              İpucu
            </div>

            <div className="mt-4 rounded-2xl border border-blue-600/15 bg-blue-600/5 p-4 text-sm text-slate-700 dark:border-blue-500/20 dark:text-slate-200">
              <div className="font-semibold">Etiketleme</div>
              <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                Kişiye özel öneri için “karmikborc1” gibi etiketler kullanın. Genel öneriler için “genel” etiketi
                ekleyin.
              </div>
              <div className="mt-3 border-t border-blue-600/10 pt-3 text-xs text-slate-600 dark:border-blue-500/20 dark:text-slate-300">
                <div className="font-semibold">Günlük görevler nereden eklenir?</div>
                <div className="mt-1">
                  Görevler bu ekrandan değil, <b>Aktivite Yönetimi</b> ekranından eklenir.
                </div>
                <div className="mt-1">
                  Aktivite etiketlerine örnek: <b>diyet</b>, <b>diyet:{draft.id || draft.title || "diyet-id"}</b>,
                  <b> diyet_program_gun:1</b>, <b>diyet_gorev_puan:3</b>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
